import React, { useState } from 'react';
import {
  BookOpen, MapPin, ClipboardList, Shield, Smartphone,
  Bot, Map, Package, ChevronDown, ChevronUp, Mail,
} from 'lucide-react';

const FEATURES = [
  {
    id: 1,
    icon: MapPin,
    color: '#10B981',
    title: 'Auto-Parse Full Address',
    subtitle: 'Paste a full address and auto-fill all fields',
    steps: [
      'Go to Work Orders and open any work order to enter the detail page.',
      'On the right side, locate the "Paste Full Address" input field.',
      'Paste a complete address into the field — e.g. 123 Main St, Los Angeles, CA 90001, USA.',
      'The system automatically splits and fills in the Street, City, State, Zip Code, and Country fields.',
      'Review each field to confirm it is correct, then save.',
    ],
    tip: 'Most standard US address formats are supported. Always verify the auto-filled fields before saving.',
  },
  {
    id: 2,
    icon: ClipboardList,
    color: '#2563EB',
    title: 'Create a Work Order',
    subtitle: 'Create and assign a new service job',
    steps: [
      'Click Work Orders in the sidebar to open the work order list.',
      'Click the "+ New Work Order" button in the top-right corner.',
      'Fill in customer information: address, phone number, and scheduled date/time.',
      'Fill in equipment details: equipment type, brand, model number, serial number, and warranty status.',
      'Describe the problem in the description field and set the priority level (Low / Medium / High / Urgent).',
      'Select the assigned technician from the dropdown menu.',
      'Click Save to create the work order. The status is automatically set to "Assigned".',
    ],
    tip: 'Once created, the assigned technician will immediately see the new job on their mobile app.',
  },
  {
    id: 3,
    icon: Shield,
    color: '#7C3AED',
    title: 'Create Roles & Set Permissions',
    subtitle: 'Admin only — manage team access levels',
    steps: [
      'An Admin account is required to access this feature.',
      'Click System → Role Management at the bottom of the sidebar.',
      'Click "+ New Role" and enter a role name (e.g. Junior Tech).',
      'Check the permission modules this role should have access to (Dashboard, Work Orders, Parts, etc.).',
      'Click Save to store the role configuration.',
      'Go to the Technicians page and edit a team member to assign them the new role.',
    ],
    tip: 'Default roles: admin (full access), manager, dispatcher, and technician. Custom roles are ideal for teams with specific access requirements.',
  },
  {
    id: 4,
    icon: Smartphone,
    color: '#0891B2',
    title: 'Technician Mobile Workflow',
    subtitle: 'Step-by-step guide for field technicians',
    steps: [
      'Log in with a technician account — the system automatically redirects you to the mobile interface.',
      'The Assigned tab shows all work orders dispatched to you, including address, priority, and scheduled time.',
      'Tap a job card, then tap "Start Job" to accept the job. The status changes to In Progress.',
      'Tap the "Navigate" button to open Google Maps with the customer\'s address pre-filled.',
      'Once on-site: tap Notes to log your diagnosis, tap Photos to upload job-site photos, and tap Parts to request any needed parts.',
      'When the job is done, tap "Complete Job", fill in the completion summary and labor cost, then confirm.',
      'The work order is automatically marked as Completed and an invoice is generated.',
    ],
    tip: 'It is recommended to take before-and-after photos at the job site for documentation and customer dispute resolution.',
  },
  {
    id: 5,
    icon: Bot,
    color: '#D97706',
    title: 'AI Diagnostic Assistant',
    subtitle: 'Get GPT-4 powered troubleshooting guidance',
    steps: [
      'Click AI Assistant in the sidebar.',
      'Enter the equipment type in the Equipment Type field (e.g. Commercial Refrigerator).',
      'Enter the brand and model number (e.g. ATOSA MBF8001GR).',
      'Describe the fault symptoms in the Problem Description field (e.g. not cooling, compressor not starting).',
      'Click "Diagnose" — the AI will generate a step-by-step troubleshooting plan and recommended solutions.',
    ],
    tip: 'The more detailed the problem description, the more accurate the AI diagnosis will be. Include any readings or observations from your inspection.',
  },
  {
    id: 6,
    icon: Map,
    color: '#059669',
    title: 'Real-Time GPS Tracking',
    subtitle: 'Monitor technician locations on the map',
    steps: [
      'Click GPS Tracking in the sidebar.',
      'The map displays the real-time location of all technicians, updated every 30 seconds.',
      'The right-side list shows each technician\'s name and status (Available / Busy).',
      'Click a technician\'s name to center the map on their current location.',
      'Map pin colors indicate status: green = available, red = busy.',
    ],
    tip: '⚠️ This feature requires a Google Maps API Key. Set REACT_APP_GOOGLE_MAPS_KEY in your .env file to enable it.',
  },
  {
    id: 7,
    icon: Package,
    color: '#DC2626',
    title: 'Parts Inventory Management',
    subtitle: 'Track stock levels and manage parts',
    steps: [
      'Click Parts Inventory in the sidebar to view all parts and stock levels.',
      'Parts with stock below the minimum threshold are highlighted in red as a low-stock alert.',
      'Click the + or - buttons on a part row to adjust the stock quantity directly.',
      'Click "+ Add Part" to add a new part: fill in part number, category, brand, cost, price, and storage location.',
      'Parts recorded on a completed work order will automatically deduct from the inventory.',
      'Use the search bar to filter parts, or check "Low Stock Only" to show only items that need restocking.',
    ],
    tip: 'Regularly check the low-stock alerts to avoid running out of critical parts during a field repair.',
  },
];

export default function GuidePage() {
  const [openId, setOpenId] = useState(1);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 0 40px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#EFF6FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BookOpen size={22} color="#2563EB" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
              User Guide
            </h1>
            <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
              Click any card below to expand step-by-step instructions.
            </p>
          </div>

          {/* Contact note */}
          <a
            href="mailto:pengyuy721@gmail.com"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              background: '#F0FDF4',
              border: '1px solid #BBF7D0',
              borderRadius: 10,
              fontSize: 12,
              color: '#166534',
              textDecoration: 'none',
              lineHeight: 1.5,
              flexShrink: 0,
            }}
          >
            <Mail size={14} color="#16A34A" style={{ flexShrink: 0 }} />
            <span>
              Questions or suggestions?<br />
              <strong>pengyuy721@gmail.com</strong> — free updates provided
            </span>
          </a>
        </div>
      </div>

      {/* Feature accordion */}
      {FEATURES.map(f => {
        const Icon = f.icon;
        const isOpen = openId === f.id;
        return (
          <div
            key={f.id}
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              marginBottom: 12,
              overflow: 'hidden',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            {/* Header */}
            <button
              onClick={() => setOpenId(isOpen ? null : f.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '16px 20px',
                background: isOpen ? '#F8FAFF' : '#fff',
                border: 'none',
                borderBottom: isOpen ? '1px solid #E5E7EB' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: f.color + '15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={20} color={f.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{f.title}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{f.subtitle}</div>
              </div>
              {isOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
            </button>

            {/* Steps body */}
            {isOpen && (
              <div style={{ padding: '20px' }}>
                <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {f.steps.map((step, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                        marginBottom: i < f.steps.length - 1 ? 14 : 0,
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: f.color,
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      >
                        {i + 1}
                      </div>
                      <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{step}</span>
                    </li>
                  ))}
                </ol>
                {f.tip && (
                  <div
                    style={{
                      marginTop: 18,
                      padding: '10px 14px',
                      background: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      borderRadius: 8,
                      fontSize: 13,
                      color: '#92400E',
                      lineHeight: 1.6,
                    }}
                  >
                    💡 {f.tip}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
